import Timeout from "@/js/component/Timeout";
import PageBlock from "@/react/story/components/PageBlock";
import PageNavigation from "@/react/story/components/PageNavigation";
import PagePublication from "@/react/story/components/PagePublication";
import TabButton from "@/react/story/components/tabs/TabButton";
import TabPane from "@/react/story/components/tabs/TabPane";
import UiInput from "@/react/story/components/ui/UiInput";
import { useErrors } from "@/react/story/providers/ErrorsContext";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useStory } from "@/react/story/providers/StoryContext";
import type { Arrows, Block, Config, StoryPage } from "@/react/story/types";
import InitUtils from "@/react/story/utils/InitUtils";
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";

const defaultTab = 1;

export default function Page() {
    const Story = useStory();
    const Items = useItems();
    const Errors = useErrors();

    const ti = useRef(new Timeout(500));
    const formRef = useRef<HTMLFormElement | null>(null);

    const [ tab, setTab ] = useState(defaultTab);

    useEffect(() => {
        setTab(defaultTab);
    }, []);

    const route = Items.current;
    if (!route) {
        return null;
    }

    const switchTopBottom = () => {
        if (route.page.bottom && route.page.top) {
            Items.savePage({ ...route.page, top: route.page.bottom, bottom: route.page.top });
        }
    };
    const saveTop = (block: Block) => Items.savePage({ ...route.page, top: block });
    const addBottom = () => {
        Items.savePage({ ...route.page, bottom: InitUtils.block(undefined) });
        setTab(2);
    };
    const removeTop = () => {
        if (route.page.bottom) {
            Items.savePage({ ...route.page, top: route.page.bottom, bottom: null });
        }
    };
    const removeBottom = () => {
        setTab(defaultTab);
        Items.savePage({ ...route.page, bottom: null });
    };
    const saveBottom = (block: Block | null) => Items.savePage({ ...route.page, bottom: block });
    const setArrows = (arrows: Arrows | null) => Items.savePage({ ...route.page, arrows });
    const setNameDelayed = (name: StoryPage["name"]) => ti.current.run(() => Items.savePage({ ...route.page, name }));
    const setConfig = (config: Partial<Config>) => Items.savePage({ ...route.page, config: { ...route.page.config, ...config } });

    const handleSubmit = (e: FormEvent<HTMLFormElement> | ChangeEvent<HTMLInputElement> | undefined) => {
        e?.preventDefault();
        submit();
    };

    const submit = () => {
        const patch: Partial<StoryPage> = {};
        if (formRef.current) {
            new FormData(formRef.current).forEach((value, key) => {
                if (!("" === value && [ "from", "to" ].includes(key))) {
                    (patch as Record<string, FormDataEntryValue>)[key] = value;
                }
            });
            Items.savePage({ ...route.page, ...patch });
        }
    };

    const handleDelete = () => {
        if (window.confirm("Voulez-vous vraiment supprimer cette page ?")) {
            Items.remove(route);
        }
    };

    const disabled = Story.story["@id"] !== route.item.owner;
    const ownerUrl = `/company/${route.page.company.replace("/api/companies/", "")}/story/${route.item.owner.replace("/api/stories/", "")}/editor`;

    return (
        <form onSubmit={handleSubmit} ref={formRef}>
            {disabled ? (
                <div className="d-flex flex-column gap-3 mb-3">
                    <div className="alert alert-warning mb-0" role="alert">
                        <p className="mb-0">
                            Cette page appartient à une story imbriquée.
                            Rendez vous sur <a href={ownerUrl}>la page d'administration de la story</a> en question
                            pour modifier son contenu.
                        </p>
                    </div>
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn btn-danger d-flex gap-1" onClick={() => Items.remove(route)}>
                            <span className="bi bi-trash"></span>
                            Retirer la story imbriquée
                        </button>
                    </div>
                </div>
            ) : null}
            <fieldset className="d-flex flex-column gap-3 p-0 bg-transparent" disabled={disabled}>
                <div className="input-group">
                    <UiInput
                        name="name"
                        className="fs-2 mb-0"
                        defaultValue={route.page.name}
                        autoFocus={true}
                        onFocus={e => e.target.select()}
                        onChange={name => name !== route.page.name && setNameDelayed(name ?? "")}
                    />
                    <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handleDelete}
                    >
                        <span className="bi-trash"></span>
                    </button>
                </div>
                <div>
                    <ul className="nav nav-underline" role="tablist">
                        <TabButton
                            active={1 === tab}
                            label={route.page.bottom ? "En haut" : "Contenu"}
                            errors={Errors.inBlock(route.page.top)}
                            onClick={() => setTab(1)}
                        />
                        {route.page.bottom ? (
                            <TabButton
                                active={2 === tab}
                                label={"En bas"}
                                errors={Errors.inBlock(route.page.bottom)}
                                onClick={() => setTab(2)}
                            />
                        ) : null}
                        <TabButton
                            active={3 === tab}
                            label="Navigation"
                            onClick={() => setTab(3)}
                        />
                        <TabButton
                            active={4 === tab}
                            label="Publication"
                            onClick={() => setTab(4)}
                        />
                        <li className="nav-item ms-auto">
                            {route.page.bottom ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={switchTopBottom}
                                >
                                    Intervertir
                                    <span className="bi-arrow-down-up ms-1"></span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-outline-success"
                                    onClick={addBottom}
                                >
                                    Ajouter un contenu
                                </button>
                            )}
                        </li>
                    </ul>
                    <hr />
                    <div className="tab-content">
                        <TabPane active={1 === tab}>
                            <PageBlock
                                block={route.page.top}
                                saveBlock={saveTop}
                                deleteBlock={route.page.bottom ? removeTop : undefined}
                            />
                        </TabPane>
                        {route.page.bottom ? (
                            <TabPane active={2 === tab}>
                                <PageBlock
                                    block={route.page.bottom}
                                    saveBlock={saveBottom}
                                    deleteBlock={removeBottom}
                                />
                            </TabPane>
                        ) : null}
                        <TabPane active={3 === tab}>
                            <PageNavigation page={route.page} setArrows={setArrows} setConfig={setConfig} />
                        </TabPane>
                        <TabPane active={4 === tab}>
                            <PagePublication page={route.page} onSubmit={() => handleSubmit(undefined)} />
                        </TabPane>
                    </div>
                </div>
            </fieldset>
        </form>
    );
}
